import { useEffect, useMemo, useRef, useState } from "react";
import { Scene } from "../types/scene";
import { useDebounce } from "./useDebounce";
import { HumansParagraph } from "../types/humansProject";
import { IActor } from "../types/actor";
import { EditorState } from "draft-js";

interface Props {
  setActiveSceneId: React.Dispatch<React.SetStateAction<number | undefined>>;
  setScenes: any;
  paragraphs: HumansParagraph[];
  scenes: Scene[];
  activeSceneId?: number;
  setParagraphs: any;
  paragraphsEditorContent: EditorState[];
  setParagraphsEditorContent: any;
  paragraphsEditorSelect: string[];
  setParagraphsEditorSelect: any;
}

interface HistoryItem {
  scenes: Scene[];
  activeSceneId: number;
  paragraphs: HumansParagraph[];
  paragraphsEditorContent: EditorState[];
  paragraphsEditorSelect: string[];
}

export const useVideoSceneHistory = ({
  scenes,
  activeSceneId,
  setActiveSceneId,
  setScenes,
  paragraphs,
  setParagraphs,
  paragraphsEditorContent,
  paragraphsEditorSelect,
  setParagraphsEditorContent,
  setParagraphsEditorSelect,
}: Props) => {
  const [history, setHistory] = useState<HistoryItem[]>([]); // array of history states
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1); // history index that changes if user switches between history states
  const editorChangeMadeByMovingInHistory = useRef<boolean>(false); // a value to define if the change was made by user or by moving in history

  // the actual state of the editor
  const currentEditorState = useMemo(() => {
    return { scenes, activeSceneId, paragraphs, paragraphsEditorSelect, paragraphsEditorContent };
  }, [scenes, activeSceneId, paragraphs, paragraphsEditorSelect, paragraphsEditorContent]);

  // console.log(currentEditorState, "currentEditorState");
  // console.log("\n\n");

  // debounced values to avoid too many history states when more than one state update action is applied to real editor state
  const {
    scenes: debouncedScenes,
    activeSceneId: debouncedActiveSceneId,
    paragraphs: debouncedParagraphs,
    paragraphsEditorSelect: debouncedParagraphsEditorSelect,
    paragraphsEditorContent: debouncedParagraphsEditorContent,
  } = useDebounce({
    value: currentEditorState,
    delay: 200,
  });

  // add new state to history but never more than 50 items
  const addToHistory = (currentEditorState: HistoryItem) => {
    setHistory((history) => [...(history.length < 51 ? history : history.slice(1)), currentEditorState]);
  };

  // update the actual editor state and reset local hook's values to default
  const removeHistoryFromNow = () => {
    setHistory((history) => history.slice(0, currentHistoryIndex + 1));
    setCurrentHistoryIndex(-1);
  };

  // update history index but never remove first state
  // in case currentHistoryIndex is -1 then go to the state which was before the last one
  const handleBackInHistory = () => {
    if (currentHistoryIndex === 0) return;
    setCurrentHistoryIndex((index) => (index > 0 ? index - 1 : history.length - 2));
  };

  // update history index but only when it's not last history state and if currentHistoryIndex was changed previously (back or forward)
  const handleForwardInHistory = () => {
    if (currentHistoryIndex > history.length - 2 || currentHistoryIndex === -1) return;
    setCurrentHistoryIndex((index) => index + 1);
  };

  // ctrl + z and ctrl + shift + z keys down handlers
  const handleKeyDown = (event: KeyboardEvent) => {
    const isCtrlZ = (event.ctrlKey || event.metaKey) && event.code === "KeyZ" && !event.shiftKey;
    const isCtrlShiftZ = (event.ctrlKey || event.metaKey) && event.code === "KeyZ" && event.shiftKey;

    if (isCtrlZ) {
      event.preventDefault();
      handleBackInHistory();
    } else if (isCtrlShiftZ) {
      event.preventDefault();
      handleForwardInHistory();
    }
  };

  // add listener for keydown, update it each time history or editor states change
  // in case any scene has text object which is being edited then ignore this change
  useEffect(() => {
    const isAnyTextInputFocused = () => {
      const activeElement = document.activeElement as HTMLElement | null;
      return (
        activeElement &&
        (activeElement.tagName === "INPUT" || activeElement.tagName === "TEXTAREA" || activeElement.isContentEditable)
      );
    };

    if (isAnyTextInputFocused() && currentEditorState.paragraphsEditorSelect.includes("Simple")) return;

    addEventListener("keydown", handleKeyDown);

    return () => removeEventListener("keydown", handleKeyDown);
  }, [history, currentHistoryIndex, currentEditorState]);

  // after each editor state change add a new item to history if anything was changed
  useEffect(() => {
    if (activeSceneId === undefined || currentHistoryIndex !== -1) return;
    const lastHistory = history[history.length - 1];
    if (
      !lastHistory ||
      JSON.stringify(lastHistory.scenes) !== JSON.stringify(debouncedScenes) ||
      JSON.stringify(lastHistory.paragraphs) !== JSON.stringify(debouncedParagraphs) ||
      JSON.stringify(lastHistory.paragraphsEditorContent) !== JSON.stringify(debouncedParagraphsEditorContent) ||
      JSON.stringify(lastHistory.paragraphsEditorSelect) !== JSON.stringify(debouncedParagraphsEditorSelect) ||
      lastHistory.activeSceneId !== debouncedActiveSceneId
    ) {
      addToHistory({
        scenes: debouncedScenes,
        activeSceneId: debouncedActiveSceneId,
        paragraphs: debouncedParagraphs,
        paragraphsEditorContent: debouncedParagraphsEditorContent,
        paragraphsEditorSelect: debouncedParagraphsEditorSelect,
      });
    }
  }, [
    debouncedScenes,
    debouncedActiveSceneId,
    debouncedParagraphs,
    debouncedParagraphsEditorContent,
    debouncedParagraphsEditorSelect,
  ]);

  // if currentHistoryIndex is being changed then update the scenes and activeSceneId displayed to user
  useEffect(() => {
    if (currentHistoryIndex >= 0) {
      setScenes(history[currentHistoryIndex].scenes);
      setActiveSceneId(history[currentHistoryIndex].activeSceneId || -1);
      setParagraphs(history[currentHistoryIndex].paragraphs);
      console.log(debouncedParagraphsEditorContent);
      console.log(paragraphsEditorContent);
      setParagraphsEditorContent(history[currentHistoryIndex].paragraphsEditorContent);
      setParagraphsEditorSelect(history[currentHistoryIndex].paragraphsEditorSelect);
      editorChangeMadeByMovingInHistory.current = true;
    }
  }, [currentHistoryIndex]);

  // reset all local hook's state and update editor state if anything in editor was changed after going back/forward
  useEffect(() => {
    if (currentHistoryIndex >= 0 && !editorChangeMadeByMovingInHistory.current) {
      removeHistoryFromNow();
    } else if (editorChangeMadeByMovingInHistory.current) {
      editorChangeMadeByMovingInHistory.current = false;
    }
  }, [currentEditorState]);

  return {
    handleBackInHistory,
    handleForwardInHistory,
    currentHistoryIndex,
    historyLength: history.length,
  };
};
